/**
 * `StatusPill` — the toggleable status chip, as ONE implementation.
 *
 * ## Why this exists
 *
 * It was the private rendering inside `StatusChipFilter`, and it now has a second
 * consumer: the Analytics "Scans by time" legend, whose rows are the same thing — a
 * status, on or off, wearing that status's colour. §36 says the SECOND hand-roll is a
 * shared component, so this is extracted rather than copied.
 *
 * The two surfaces mean different things by "on", and that is fine because the shape is
 * identical:
 *
 * | surface | selected means | unselected means |
 * |---|---|---|
 * | Filters drawer | INCLUDED in the query | excluded |
 * | Chart legend | SHOWN in the chart | hidden |
 *
 * Both are a binary over the same vocabulary with the same colour source, so a reader who
 * has learned one has learned the other. What must NOT happen is a second pill design
 * tuned to look similar — that is the drift this codebase has paid for repeatedly (see the
 * `statusTone` note in `StatusChipFilter`, where a filter and the table it filtered painted
 * the same status two different colours).
 *
 * ## Why the whole filter was not simply imported instead
 *
 * `StatusChipFilter` is a FILTER FIELD: it owns an options array, selection state, the
 * `{field, operator, value}` emit contract and the drawer's label/clear row. The legend
 * needs one pill. Importing the field to get the pill would have dragged all of that into a
 * chart card.
 *
 * ## It is a real `<button>`, which the extracted original was not
 *
 * The drawer rendered a `Stack` (a `div`) with an `onClick`: not focusable, not
 * operable by Enter/Space, and with no machine-readable on/off state. The legend already
 * had this right, so the extraction takes the better of the two behaviours and BOTH
 * surfaces get it — `aria-pressed` carries the state and `:focus-visible` shows focus,
 * since the tint is a visual channel only.
 *
 * ⚠️ Swapping `div` for `button` means the UA's own button chrome has to be reset or the
 * computed styles change. `font`, `color`, `margin`, `textAlign`, `appearance` and the
 * default `outline` are all reset below, and the drawer's rendered output was measured
 * before and after to confirm it did not move.
 */
import React from 'react'

import { Stack, Tooltip, Typography, alpha, darken, lighten, useTheme } from '@mui/material'

import { statusTone } from 'components/display/StatusChip/StatusChip'

/**
 * `pill` is the small status token (the filters drawer, the chart legend). `badge` is the
 * bigger rounded-rect capability badge matching the product page's `TagCapabilityBadges`.
 * Both were already in `StatusChipFilter` and both move here unchanged.
 */
export type StatusPillVariant = 'pill' | 'badge'

export interface StatusPillOption {
  value?: string
  label: string
  /** Status hue — drives the selected fill/border/text and the icon colour. */
  color: string
  /** Exact text/icon colour for the `badge` variant; falls back to a lighten/darken. */
  textColor?: string
  /** The status's AUTHORED fill and edge, straight from the STATUSES map. */
  backgroundColor?: string
  borderColor?: string
  icon?: React.ReactElement
}

export interface StatusPillProps {
  option: StatusPillOption
  /** ON. The drawer reads this as "in the query"; the legend as "drawn in the chart". */
  selected: boolean
  onClick?: () => void
  variant?: StatusPillVariant
  /** Hover text. Omit for none — an empty title renders no tooltip. */
  tooltip?: string
  /**
   * Accessible name. Defaults to the label, which is right for the drawer. The legend
   * overrides it to name the THING ("Active series") — the on/off state is `aria-pressed`'s
   * job, and announcing both is how a toggle ends up read out contradicting itself.
   */
  ariaLabel?: string
  /**
   * Force the leading icon's colour, instead of inheriting the pill's current text colour.
   *
   * For the chart legend, where the icon is the SERIES SWATCH and has to be the exact
   * stroke the chart draws — a legend whose dot is a near-but-not-equal shade of the line
   * is worse than no dot. The drawer passes nothing and keeps the inherit behaviour, where
   * the icon is a decoration on the label rather than a key to something else on screen.
   */
  iconColor?: string
}

export const StatusPill = ({
  option,
  selected,
  onClick,
  variant = 'pill',
  tooltip,
  ariaLabel,
  iconColor,
}: StatusPillProps) => {
  const theme = useTheme()
  const isBadge = variant === 'badge'
  const dark = theme.palette.mode === 'dark'

  // Badge variant: match the product-card TagCapabilityBadges exactly — a light
  // family-colour tint for the text/icon (not the saturated base), over an alpha fill +
  // alpha border. Pill variant keeps the solid base colour.
  const badgeText =
    option.textColor ?? (dark ? lighten(option.color, 0.6) : darken(option.color, 0.45))
  // `statusTone(...).color`, not the raw `option.color`. The hue in a STATUSES entry is the
  // LIGHT-mode text colour; in dark, StatusChip lightens it so the label reads on a dark
  // translucent fill. Using the raw hue left the filter's label a dark green on a dark chip
  // while the table's badge showed the lightened green.
  const onText = isBadge ? badgeText : statusTone(option, dark).color

  return (
    <Tooltip title={tooltip ?? ''} placement="bottom" arrow>
      {/*
        A real <button>, not a Stack with an onClick — see the file header. `type="button"`
        so a pill inside a form (the drawer is not one today, but the legend's card could
        become one) never submits it.
      */}
      <Stack
        component="button"
        type="button"
        direction="row"
        alignItems="center"
        spacing={2}
        onClick={onClick}
        aria-pressed={selected}
        aria-label={ariaLabel ?? option.label}
        sx={{
          // pill: status token (leading icon + status color), roomier tap target
          //   in the filter drawer. badge: the bigger capability badge (rounded-rect,
          //   34px) matching the product-page TagCapabilityBadges md size.
          px: isBadge ? 2.5 : 4,
          py: isBadge ? 2 : 2.5,
          // Badge chips: fixed height so they line up with the multiSelect filter
          // chips (Size, Best for) — every filter chip is the same height.
          ...(isBadge && { minHeight: 38, py: 0 }),
          cursor: 'pointer',
          borderRadius: isBadge ? 2 : '999px',
          // Constant 1px border + fixed padding in BOTH states — only the colors
          // change on select, so the chip never grows/shifts (the perceived "gap").
          border: '1px solid',
          /**
           * ── UA BUTTON CHROME RESET ──────────────────────────────────────────────
           * Everything a `<button>` gets for free that a `div` does not. Without these
           * the extraction would have silently restyled every status filter in the app:
           * the UA supplies its own font (13.333px system), centred text, a margin in
           * some engines, and `appearance: auto`. Measured before/after on the
           * Certificates drawer to confirm all of it lands back on the div's values.
           */
          font: 'inherit',
          margin: 0,
          textAlign: 'start',
          appearance: 'none',
          // `statusTone` — the SAME function StatusChip uses for the table's Status column,
          // so a selected pill is by construction the same colour as the status it names.
          //
          // `isBadge` keeps its own ramp: those are capability badges, not statuses, so they
          // have no STATUSES entry and no tone to inherit.
          borderColor: selected
            ? isBadge
              ? alpha(option.color, dark ? 0.45 : 0.32)
              : statusTone(option, dark).border
            : theme.palette.grey[300],
          backgroundColor: selected
            ? isBadge
              ? alpha(option.color, dark ? 0.22 : 0.1)
              : statusTone(option, dark).background
            : 'transparent',
          color: selected ? onText : theme.palette.text.primary,
          transition: 'background-color .12s, border-color .12s, color .12s',
          '&:hover': {
            borderColor: isBadge ? option.color : statusTone(option, dark).border,
            backgroundColor: selected
              ? isBadge
                ? alpha(option.color, 0.18)
                : statusTone(option, dark).background
              : alpha(option.color, 0.08),
          },
          /**
           * The keyboard half, and it is NOT optional: the on/off state is carried
           * visually by the fill, so a keyboard user with no focus indicator cannot tell
           * which pill they are about to toggle. `:focus-visible` rather than `:focus` so
           * a mouse click does not leave a ring behind. Same brand ring the fields use
           * (§31), so focus looks the same here as on an input.
           */
          '&:focus-visible': {
            outline: 'none',
            borderColor: theme.palette.brand[600],
            boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
          },
          // Icon inherits the chip's current text color — unless the caller pins it,
          // which the legend does because its icon is the series swatch.
          '& svg': {
            color: iconColor ?? (selected ? onText : theme.palette.text.secondary),
            width: 16,
            height: 16,
          },
        }}
      >
        {option.icon}
        <Typography sx={{ fontSize: isBadge ? 14 : 13, fontWeight: 500, lineHeight: 1 }}>
          {option.label}
        </Typography>
      </Stack>
    </Tooltip>
  )
}
